/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for Users
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authenticatedApi, unwrapResponse } from "@/api/authenticatedClient";
import type { ApiResponse, PaginatedData, User, Role, Permission } from "@/lib/types";

// ============================================================================
// Types
// ============================================================================
interface BackendUserListResponse {
  items: BackendUser[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface BackendUser {
  id: string;
  email: string;
  full_name?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
  job_title?: string | null;
  department?: string | null;
  organization?: string | null;
  role?: string; // Direct role string from /auth/me
  is_super_user?: boolean;
  state_codes?: string[];
  permissions?: string[];
  primary_role_id?: string;
  primary_role?: {
    id: string;
    name: string;
    display_name?: string;
    is_active?: boolean;
  };
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  last_login_at?: string | null;
  // Allow additional unknown fields
  [key: string]: unknown;
}

export interface UserListItem {
  id: string;
  email: string;
  fullName: string;
  department?: string;
  jobTitle?: string;
  primaryRole: {
    id: string;
    name: string;
    displayName: string;
  };
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  // IdP fields from GET /users (UserSummary) — used by Admin > Identity Providers > Users tab
  externalUserId?: string;
  idpConnectionId?: string;
  idpConnectionName?: string;
}

export interface CreateUserInput {
  email: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  department?: string;
  jobTitle?: string;
  primaryRoleId: string;
}

export interface UpdateUserInput {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  department?: string;
  jobTitle?: string;
  primaryRoleId?: string;
  isActive?: boolean;
}

export interface UserFilters {
  search?: string;
  roleId?: string;
  department?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  permissions: (userId: string) => [...userKeys.detail(userId), "permissions"] as const,
  current: () => [...userKeys.all, "current"] as const,
};

/** Role names that grant admin access (must match backend require_role). Use assigned role from user table. */
export const ADMIN_ROLE_NAMES = ["system_admin", "state_admin"] as const;

/** Role name for state administrator (super admin); only this role can access Change Requests admin page. */
export const STATE_ADMIN_ROLE_NAME = "state_admin";

/** True if the given role (from /auth/me – assigned role name) is an admin role. */
export function isAdminRole(role: string | undefined): boolean {
  if (!role || typeof role !== "string") return false;
  return ADMIN_ROLE_NAMES.includes(role.trim().toLowerCase() as (typeof ADMIN_ROLE_NAMES)[number]);
}

/** Display name for state administrator (may be returned by /auth/me instead of internal name). */
const STATE_ADMIN_DISPLAY_NAME = "state administrator";

/** True if the given role is state administrator (only role that can review/approve change requests). */
export function isStateAdminRole(role: string | undefined): boolean {
  if (!role || typeof role !== "string") return false;
  const r = role.trim().toLowerCase();
  return r === STATE_ADMIN_ROLE_NAME || r === STATE_ADMIN_DISPLAY_NAME;
}

export const roleKeys = {
  all: ["roles"] as const,
  list: () => [...roleKeys.all, "list"] as const,
  detail: (id: string) => [...roleKeys.all, "detail", id] as const,
  permissions: (roleId: string) => [...roleKeys.detail(roleId), "permissions"] as const,
};

// ============================================================================
// User Query Hooks
// ============================================================================

/**
 * Fetch current user
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: userKeys.current(),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<BackendUser>>("/auth/me");
      const userData = unwrapResponse(response);

      // Transform backend snake_case to frontend camelCase
      const rawData: BackendUser = userData;

      // Role from user table: backend sends role (auth.roles.name, e.g. "state_admin") and role_display_name for UI.
      const internalRoleName =
        (typeof rawData.role === "string" ? rawData.role : "") || rawData.primary_role?.name || "";
      const roleDisplayName =
        (rawData.role_display_name as string) ||
        rawData.primary_role?.display_name ||
        internalRoleName;

      const transformedUser: User = {
        id: rawData.id || "",
        email: rawData.email || "",
        fullName: rawData.full_name || "",
        displayName: rawData.display_name || rawData.full_name || "",
        avatarUrl: rawData.avatar_url || undefined,
        avatar: rawData.avatar_url || undefined, // Backward compatibility
        // Use internal role name from user table for permission/visibility checks (e.g. isStateAdminRole).
        role: internalRoleName,
        primaryRoleId: rawData.primary_role_id || "",
        primaryRole: {
          id: rawData.primary_role_id || "",
          name: internalRoleName,
          displayName: roleDisplayName,
          isSuperUser: rawData.is_super_user ?? false,
          isSystemRole: true,
          isActive: true,
        },
        isActive: rawData.is_active ?? true,
        permissions: Array.isArray(rawData.permissions) ? rawData.permissions : [],
        createdAt: rawData.created_at || "",
        isSuperUser: rawData.is_super_user ?? false,
      };

      return transformedUser;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // Don't retry auth requests
  });
}

/**
 * Fetch current user's permissions
 */
export function useCurrentUserPermissions() {
  return useQuery({
    queryKey: [...userKeys.current(), "permissions"],
    queryFn: async () => {
      const response =
        await authenticatedApi.get<ApiResponse<Permission[]>>("/auth/me/permissions");
      return unwrapResponse(response);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch paginated list of users (admin)
 */
export interface UseUsersOptions {
  enabled?: boolean;
}

export function useUsers(filters: UserFilters = {}, options: UseUsersOptions = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: userKeys.list(filters),
    enabled,
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<BackendUserListResponse>>("/users/", {
        params: {
          search: filters.search,
          role: filters.roleId,
          status:
            filters.isActive === true
              ? "active"
              : filters.isActive === false
                ? "inactive"
                : undefined,
          page: filters.page,
          pageSize: filters.pageSize,
        },
      });
      const raw = unwrapResponse(response);
      const items: BackendUser[] = Array.isArray(raw.items) ? raw.items : [];
      const transformed: PaginatedData<UserListItem> = {
        data: items.map((u) => ({
          id: u.id || "",
          email: u.email || "",
          fullName: u.full_name || u.display_name || u.email || "",
          department: u.department || undefined,
          jobTitle: u.job_title || undefined,
          // GET /users (users/service.py) returns role_display_name + role_name as flat strings.
          // GET /auth/me returns primary_role as a nested object.
          // Handle both shapes via the BackendUser catch-all [key: string]: unknown.
          primaryRole: {
            id: u.primary_role?.id || (u.primary_role_id as string) || "",
            name: u.primary_role?.name || (u.role_name as string) || "",
            displayName:
              u.primary_role?.display_name ||
              (u.role_display_name as string) ||
              (u.role_name as string) ||
              u.primary_role?.name ||
              "",
          },
          isActive: u.is_active ?? true,
          lastLoginAt: u.last_login_at || undefined,
          createdAt: u.created_at || "",
          // IdP fields from GET /users (users/service.py UserSummary).
          // Used by Admin > Identity Providers > Users tab.
          externalUserId: (u.external_user_id as string) || undefined,
          idpConnectionId: (u.idp_connection_id as string) || undefined,
          idpConnectionName: (u.idp_connection_name as string) || undefined,
        })),
        pagination: {
          total: raw.total,
          page: raw.page,
          pageSize: raw.page_size,
          totalPages: raw.total_pages,
        },
      };
      return transformed;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch user detail by ID
 */
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: userKeys.detail(id || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<BackendUser>>(`/users/${id ?? ""}`);
      const rawData = unwrapResponse(response);

      // Transform backend response to match frontend types
      // Backend returns snake_case fields, convert to camelCase
      const transformedUser: Partial<User> & {
        id: string;
        email: string;
        fullName: string;
        isActive: boolean;
        primaryRoleId: string;
        createdAt: string;
        updatedAt?: string;
        primaryRole?: Role;
        role?: string;
        organization?: string;
      } = {
        // Preserve all original fields
        id: rawData.id || "",
        email: rawData.email || "",
        isActive: rawData.is_active ?? true,
        primaryRoleId: rawData.primary_role_id || "",
        lastLoginAt: rawData.last_login_at || undefined,
        createdAt: rawData.created_at || "",
        updatedAt: rawData.updated_at || undefined,
        firstName: rawData.first_name || undefined,
        lastName: rawData.last_name || undefined,
        displayName: rawData.display_name || undefined,
        jobTitle: rawData.job_title || undefined,
        fullName: rawData.full_name || "",
        department: rawData.department || undefined,
        organization: rawData.department || rawData.organization || undefined,
        primaryRole: undefined,
        role: undefined,
      };

      // Transform role object if present
      if (rawData.primary_role) {
        transformedUser.primaryRole = {
          id: rawData.primary_role.id || "",
          name: rawData.primary_role.name || "",
          displayName: rawData.primary_role.display_name || rawData.primary_role.name || "",
          description: undefined,
          isSuperUser: rawData.is_super_user ?? false,
          isSystemRole: false,
          isActive: rawData.primary_role.is_active ?? true,
        };
        // Keep role as string for backward compatibility
        transformedUser.role =
          typeof rawData.role === "string"
            ? rawData.role
            : rawData.primary_role.display_name || rawData.primary_role.name || "";
      } else if (rawData.role && typeof rawData.role === "string") {
        // Fallback: role as direct string (e.g., from /auth/me)
        transformedUser.role = rawData.role;
      }

      return transformedUser;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch user's permissions
 */
export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: userKeys.permissions(userId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Permission[]>>(
        `/users/${userId ?? ""}/permissions`
      );
      return unwrapResponse(response);
    },
    enabled: !!userId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// ============================================================================
// User Mutation Hooks
// ============================================================================

/**
 * Create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const response = await authenticatedApi.post<ApiResponse<User>>("/users/", input);
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateUserInput }) => {
      const response = await authenticatedApi.patch<ApiResponse<User>>(`/users/${id}`, data);
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Update current user's own profile
 */
export function useUpdateCurrentUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      firstName?: string;
      lastName?: string;
      displayName?: string;
      department?: string;
      jobTitle?: string;
      phone?: string;
    }) => {
      const response = await authenticatedApi.patch<ApiResponse<User>>("/users/me", data);
      return unwrapResponse(response);
    },
    onSuccess: (_result, _variables) => {
      // Invalidate current user query
      void queryClient.invalidateQueries({ queryKey: userKeys.current() });
      // Also invalidate user detail query if we have the user ID
      // This ensures the profile page refreshes with updated data
      void queryClient.invalidateQueries({ queryKey: userKeys.details() });
    },
  });
}

/**
 * Deactivate a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await authenticatedApi.post<ApiResponse<User>>(`/users/${id}/deactivate`);
      return unwrapResponse(response);
    },
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Reactivate a user
 */
export function useReactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await authenticatedApi.post<ApiResponse<User>>(`/users/${id}/reactivate`);
      return unwrapResponse(response);
    },
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

// ============================================================================
// Role Query Hooks
// ============================================================================

/**
 * Fetch all roles
 */
export function useRoles() {
  return useQuery({
    queryKey: roleKeys.list(),
    queryFn: async () => {
      const response =
        await authenticatedApi.get<
          ApiResponse<{ roles: Record<string, unknown>[]; total: number }>
        >("/users/meta/roles");
      const data = unwrapResponse(response);
      // Backend returns snake_case; transform to camelCase Role type
      return data.roles.map((r): Role => ({
        id: (r.id as string) || "",
        name: (r.name as string) || "",
        displayName: (r.display_name as string) || (r.name as string) || "",
        description: (r.description as string) || undefined,
        isSuperUser: r.is_super_user === true,
        isSystemRole: r.is_system_role === true,
        isActive: r.is_active !== false,
      }));
    },
    staleTime: 5 * 60 * 1000, // Roles rarely change
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch role detail
 */
export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: roleKeys.detail(id || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Role>>(`/roles/${id ?? ""}`);
      return unwrapResponse(response);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Fetch role's permission codes from GET /users/meta/roles/:id/permissions
 */
export function useRolePermissions(roleId: string | undefined) {
  return useQuery({
    queryKey: roleKeys.permissions(roleId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<
        ApiResponse<{ role_id: string; role_name: string; permission_codes: string[] }>
      >(`/users/meta/roles/${roleId ?? ""}/permissions`);
      const data = unwrapResponse(response);
      return data.permission_codes;
    },
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// ============================================================================
// Permissions Query Hooks (for Permissions Matrix UI)
// ============================================================================

export interface PermissionItem {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
}

export const permissionKeys = {
  all: ["permissions"] as const,
  list: () => [...permissionKeys.all, "list"] as const,
};

/**
 * Fetch all permissions for the Permissions Matrix UI
 */
export function useAllPermissions() {
  return useQuery({
    queryKey: permissionKeys.list(),
    queryFn: async () => {
      const response =
        await authenticatedApi.get<ApiResponse<{ permissions: PermissionItem[]; total: number }>>(
          "/users/meta/permissions"
        );
      const data = unwrapResponse(response);
      return data.permissions;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

// ============================================================================
// Sessions Query Hooks
// ============================================================================
interface BackendSession {
  id: string;
  user_id: string;
  ip_address?: string | null;
  user_agent?: string | null;
  device_info?: Record<string, unknown> | null;
  created_at: string;
  expires_at?: string | null;
  is_active?: boolean;
  is_current?: boolean;
}

export const sessionKeys = {
  all: ["sessions"] as const,
  current: () => [...sessionKeys.all, "current"] as const,
};

/**
 * Fetch active sessions for current user
 */
export function useSessions() {
  return useQuery({
    queryKey: sessionKeys.current(),
    queryFn: async () => {
      const response =
        await authenticatedApi.get<ApiResponse<{ sessions: BackendSession[]; total: number }>>(
          "/auth/sessions"
        );
      const data = unwrapResponse(response);

      return data.sessions.map((session) => ({
        id: session.id || "",
        userId: session.user_id || "",
        ipAddress: session.ip_address || undefined,
        userAgent: session.user_agent || undefined,
        deviceInfo: session.device_info || undefined,
        createdAt: session.created_at || "",
        expiresAt: session.expires_at || undefined,
        isActive: session.is_active ?? true,
        isCurrent: session.is_current ?? false,
      }));
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
}

// ============================================================================
// Role Mutation Hooks
// ============================================================================

/**
 * Create a new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      name: string;
      displayName: string;
      description?: string;
      parentRoleId?: string;
    }) => {
      const response = await authenticatedApi.post<ApiResponse<Role>>("/roles", input);
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.list() });
    },
  });
}

/**
 * Update a role
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: { displayName?: string; description?: string; parentRoleId?: string };
    }) => {
      const response = await authenticatedApi.patch<ApiResponse<Role>>(`/roles/${id}`, data);
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: roleKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: roleKeys.list() });
    },
  });
}

/**
 * Update role permissions via PUT /users/meta/roles/:id/permissions
 */
export function useUpdateRolePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      roleId,
      permissionCodes,
    }: {
      roleId: string;
      permissionCodes: string[];
    }) => {
      const response = await authenticatedApi.put<
        ApiResponse<{ role_id: string; role_name: string; permission_codes: string[] }>
      >(`/users/meta/roles/${roleId}/permissions`, {
        permission_codes: permissionCodes,
      });
      return unwrapResponse(response);
    },
    onSuccess: (_, { roleId }) => {
      void queryClient.invalidateQueries({
        queryKey: roleKeys.permissions(roleId),
      });
      // Users holding this role have stale effective permissions until refreshed — invalidate the
      // users cache (incl. current-user permissions) so access-gated UI updates without a reload.
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
